/**
 * Frontend AI Cost & Pricing Utilities
 */

export interface ModelPricingCatalogItem {
  modelId: string
  provider: string
  displayName: string
  inputCostPerMillion: number
  outputCostPerMillion: number
  inputCostPer1K: number
  outputCostPer1K: number
  category: 'chat' | 'audio' | 'embedding' | 'system_one'
  description: string
  isFreeTier?: boolean
  examplePromptCost1K: number
  exampleCompletionCost500: number
}

export interface PricingCatalogResponse {
  catalog: ModelPricingCatalogItem[]
  defaultRate: {
    inputCostPerMillion: number
    outputCostPerMillion: number
  }
  formula: string
  freeTierRules: string[]
  updatedAt: string
}

/**
 * Formats a dollar amount dynamically according to its magnitude:
 * - $12.45 (2 decimals for >= $1)
 * - $0.045 (3 decimals for >= $0.01)
 * - $0.00045 (5 decimals for < $0.01)
 * - $0.00 for 0
 */
export function formatCostUSD(amount: number | null | undefined, options?: { showFreeBadge?: boolean }): string {
  if (amount === null || amount === undefined || isNaN(amount) || amount === 0) {
    return options?.showFreeBadge ? '$0.00 (Free)' : '$0.00'
  }

  if (amount >= 1) {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  if (amount >= 0.01) {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`
  }

  if (amount >= 0.0001) {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`
  }

  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}`
}

/**
 * Formats token counts nicely (e.g. 1.2M, 45.3k, 850)
 */
export function formatTokenCount(tokens: number | null | undefined): string {
  if (!tokens || tokens <= 0) return '0'
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(2)}M`
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}k`
  }
  return tokens.toLocaleString('en-US')
}
